<template>
  <div class="pref">
    <h1 class="pref-h1">
      Date Availability
    </h1>
    <p class="pref-lead">
      When you can host dates: the blocks you set aside for Breeze, and whether you take group dates.
    </p>

    <div class="pref-stack">
      <ds-tabs
        v-model="activeTab"
        :tabs="tabs"
        id-prefix="pref"
      />

      <!-- v-show, not v-if: both panels stay mounted so unsaved block edits survive a
           glance at the other tab, which is what b-tabs did before. One panel per tab with
           a static id, so every tab's aria-controls resolves rather than only the active
           one's. -->
      <div
        v-show="activeTab === 'one-on-one'"
        id="pref-panel-one-on-one"
        class="pref-panel"
        role="tabpanel"
        aria-labelledby="pref-tab-one-on-one"
        tabindex="0"
      >
        <date-capacity
          :location-id="location.id"
          :time-slots="dateCapacityTimeSlots"
          :duration-minutes="dateCapacityDuration"
          :gap-minutes="dateCapacityArrivalGap"
          :deal-id="dateCapacityDealId"
          :reservation-method="location.reservationMethod"
        />
      </div>
      <div
        v-show="activeTab === 'group'"
        id="pref-panel-group"
        class="pref-panel"
        role="tabpanel"
        aria-labelledby="pref-tab-group"
        tabindex="0"
      >
        <group-date-availability :location-id="location.id" />
      </div>
    </div>
  </div>
</template>

<script>

import { DsTabs } from '@/design-system/components'
import GroupDateAvailability from '@/components/account/GroupDateAvailability.vue'
import DateCapacity from '@/components/account/capacity/DateCapacity.vue'
import { mapGetters } from 'vuex'

export default {
  components: {
    DsTabs,
    GroupDateAvailability,
    DateCapacity,
  },
  data() {
    return {
      activeTab: 'one-on-one',
      tabs: [
        { key: 'one-on-one', label: '1-on-1 dates' },
        { key: 'group', label: 'Group dates' },
      ],
    }
  },
  computed: {
    ...mapGetters({
      location: 'authentication/location',
    }),
    // Time slots and the duration/gap settings live on the deals, not the location. The tab
    // reads them from the visible (non-group-date) deals: slots from all of them, settings
    // (and the dealId the save writes back to) from the assignable deal, since that's the one
    // couples can actually be booked into. Fall back to the first visible deal if none is
    // flagged assignable.
    primaryCapacityDeal() {
      const visible = this.getVisibleDeals(this.location.deals)
      return visible.find(deal => deal.assignable) || visible[0] || null
    },
    dateCapacityTimeSlots() {
      return this.getVisibleDeals(this.location.deals)
        .reduce((acc, deal) => acc.concat(deal.timeSlots || []), [])
    },
    dateCapacityDuration() {
      const deal = this.primaryCapacityDeal
      return deal && deal.durationMinutes != null ? deal.durationMinutes : 90
    },
    dateCapacityArrivalGap() {
      const deal = this.primaryCapacityDeal
      return deal && deal.gapMinutes != null ? deal.gapMinutes : 0
    },
    dateCapacityDealId() {
      return this.primaryCapacityDeal ? this.primaryCapacityDeal.id : null
    },
  },
  methods: {
    getVisibleDeals(deals) {
      // `location` resolves to {} while the partner is still loading, so `deals`
      // can be undefined on the first render. Guard against it.
      return (deals || []).filter(deal => deal.deprecatedAt == null && deal.dealType.active && deal.dealType.uniqueName !== 'group_date')
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/design-system/mixins';

.pref {
  max-width: 1140px;
  margin: 0 auto;
  color: $c-text;
}

.pref-h1 {
  @include ds-page-title;
}

.pref-lead {
  @include ds-lead;
}

.pref-stack {
  @include ds-stack;

  align-items: stretch;
}

// Keeps the segmented control from stretching across the stack.
.ds-tabs {
  align-self: flex-start;
}

</style>
